import { Fragment, useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { backendHOST } from '../../../../client-config/client-config';

import { logout } from '../../../../actions/auth';
import { withRouter } from '../../../hooks/withRouter';

const url = `${backendHOST}/api/scriptantiaAdmin`;

const FormEredmeny = ({ auth }) => {
  const [rows, setRows] = useState([]);
  const [groupMode, setGroupMode] = useState('schoolYear');
  const [searchText, setSearchText] = useState('');

  const [loading, setLoading] = useState(false);

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const authHeaders = {
    'Content-Type': 'application/json',
    'x-auth-token': localStorage.token,
  };
  const getGroupLabel = (row) => {
    const period = parsePeriod(row.idoszak);

    if (groupMode === 'schoolYear') {
      return period.schoolYear;
    }

    if (groupMode === 'calendarYear') {
      return period.calendarYear;
    }

    return null;
  };

  const getGroupTitle = () => {
    if (groupMode === 'schoolYear') return 'Tanév';
    if (groupMode === 'calendarYear') return 'Naptári év';
    return '';
  };
  const formatMoney = (value) => {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const number = Number(value);

    if (Number.isNaN(number)) {
      return String(value);
    }

    return `${new Intl.NumberFormat('hu-HU', {
      maximumFractionDigits: 0,
    }).format(number)} Ft`;
  };

  const numberValue = (value) => {
    const number = Number(value || 0);

    return Number.isNaN(number) ? 0 : number;
  };

  const sumField = (items, field) => {
    return items.reduce((sum, row) => sum + numberValue(row[field]), 0);
  };

  const getAmountColor = (value, inverse = false) => {
    const number = numberValue(value);

    if (number === 0) {
      return 'text.primary';
    }

    if (inverse) {
      return number > 0 ? 'warning.main' : 'success.main';
    }

    return number >= 0 ? 'success.main' : 'warning.main';
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${url}/Eredmeny`, {
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error('Nem sikerült betölteni az eredménykimutatást.');
      }

      const data = await response.json();

      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Ismeretlen hiba történt betöltés közben.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const parsePeriod = (idoszak) => {
    if (!idoszak) {
      return {
        year: 0,
        month: 0,
        periodSort: 0,
        calendarYear: 'Nincs év',
        schoolYear: 'Nincs tanév',
      };
    }

    const normalized = String(idoszak).trim();

    const match = normalized.match(/^(\d{4})[./-](\d{1,2})$/);

    if (!match) {
      return {
        year: 0,
        month: 0,
        periodSort: 0,
        calendarYear: 'Nincs év',
        schoolYear: 'Nincs tanév',
      };
    }

    const year = Number(match[1]);
    const month = Number(match[2]);

    const schoolYearStart = month >= 9 ? year : year - 1;
    const schoolYearEnd = schoolYearStart + 1;

    return {
      year,
      month,
      periodSort: year * 100 + month,
      calendarYear: String(year),
      schoolYear: `${schoolYearStart}/${schoolYearEnd}`,
    };
  };
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const periodA = parsePeriod(a.idoszak);
      const periodB = parsePeriod(b.idoszak);

      return periodB.periodSort - periodA.periodSort;
    });
  }, [rows]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    if (!normalizedSearch) {
      return sortedRows;
    }

    return sortedRows.filter((row) =>
      [
        row.idoszak,
        row.Oradijak,
        row.Berek,
        row.Koltsegek,
        row.Eredmeny,
        row.befizetes_sum,
        row.kiadasok_sum,
        row.CashFlow,
        row.Kintlevoseg,
        row.Tamogatas,
        row.Veszteseg,

        row.BefizetesKup,
        row.BefizetesUtal,
        row.BefizetesKp,
        row.KifizetesUtal,
        row.KifizetesKp,
        row.KoltsegUt,
        row.KoltsegKp,
        row.Penztar,
        row.OktatoiTartozas,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearch),
        ),
    );
  }, [sortedRows, searchText]);

  const totals = useMemo(() => {
    return {
      Oradijak: sumField(filteredRows, 'Oradijak'),
      Berek: sumField(filteredRows, 'Berek'),
      Koltsegek: sumField(filteredRows, 'Koltsegek'),
      Eredmeny: sumField(filteredRows, 'Eredmeny'),
      befizetes_sum: sumField(filteredRows, 'befizetes_sum'),
      kiadasok_sum: sumField(filteredRows, 'kiadasok_sum'),
      CashFlow: sumField(filteredRows, 'CashFlow'),
      Kintlevoseg: sumField(filteredRows, 'Kintlevoseg'),
      Tamogatas: sumField(filteredRows, 'Tamogatas'),
      Veszteseg: sumField(filteredRows, 'Veszteseg'),

      BefizetesKup: sumField(filteredRows, 'BefizetesKup'),
      BefizetesUtal: sumField(filteredRows, 'BefizetesUtal'),
      BefizetesKp: sumField(filteredRows, 'BefizetesKp'),
      KifizetesUtal: sumField(filteredRows, 'KifizetesUtal'),
      KifizetesKp: sumField(filteredRows, 'KifizetesKp'),
      KoltsegUt: sumField(filteredRows, 'KoltsegUt'),
      KoltsegKp: sumField(filteredRows, 'KoltsegKp'),
      Penztar: sumField(filteredRows, 'Penztar'),
      OktatoiTartozas: sumField(filteredRows, 'OktatoiTartozas'),
    };
  }, [filteredRows]);
  const groupedRows = useMemo(() => {
    if (groupMode === 'none') {
      return {
        '': filteredRows,
      };
    }

    return filteredRows.reduce((groups, row) => {
      const groupLabel = getGroupLabel(row) || 'Nincs csoport';

      if (!groups[groupLabel]) {
        groups[groupLabel] = [];
      }

      groups[groupLabel].push(row);

      return groups;
    }, {});
  }, [filteredRows, groupMode]);

  const groupedEntries = useMemo(() => {
    return Object.entries(groupedRows).sort(([groupA], [groupB]) => {
      if (groupMode === 'schoolYear') {
        const startA = Number(String(groupA).slice(0, 4)) || 0;
        const startB = Number(String(groupB).slice(0, 4)) || 0;

        return startB - startA;
      }

      if (groupMode === 'calendarYear') {
        return Number(groupB) - Number(groupA);
      }

      return 0;
    });
  }, [groupedRows, groupMode]);

  const getGroupTotals = (items) => ({
    Oradijak: sumField(items, 'Oradijak'),
    Berek: sumField(items, 'Berek'),
    Koltsegek: sumField(items, 'Koltsegek'),
    Eredmeny: sumField(items, 'Eredmeny'),
    befizetes_sum: sumField(items, 'befizetes_sum'),
    kiadasok_sum: sumField(items, 'kiadasok_sum'),
    CashFlow: sumField(items, 'CashFlow'),
    Kintlevoseg: sumField(items, 'Kintlevoseg'),
    Tamogatas: sumField(items, 'Tamogatas'),
    Veszteseg: sumField(items, 'Veszteseg'),

    BefizetesKup: sumField(items, 'BefizetesKup'),
    BefizetesUtal: sumField(items, 'BefizetesUtal'),
    BefizetesKp: sumField(items, 'BefizetesKp'),
    KifizetesUtal: sumField(items, 'KifizetesUtal'),
    KifizetesKp: sumField(items, 'KifizetesKp'),
    KoltsegUt: sumField(items, 'KoltsegUt'),
    KoltsegKp: sumField(items, 'KoltsegKp'),
    Penztar: sumField(items, 'Penztar'),
    OktatoiTartozas: sumField(items, 'OktatoiTartozas'),
  });
  const latestRow = filteredRows[0] || null;

  const exportRows = () => {
    const columns = [
      ['Időszak', 'idoszak'],
      ['Óradíjak', 'Oradijak'],
      ['Bérek', 'Berek'],
      ['Költségek', 'Koltsegek'],
      ['Eredmény', 'Eredmeny'],
      ['Befizetések', 'befizetes_sum'],
      ['Kiadások', 'kiadasok_sum'],
      ['Cash-Flow', 'CashFlow'],
      ['Kintlévőség', 'Kintlevoseg'],
      ['Támogatás', 'Tamogatas'],
      ['Veszteség', 'Veszteseg'],

      ['Kupon felhasználás', 'BefizetesKup'],
      ['Befizetés - Utalás', 'BefizetesUtal'],
      ['Befizetés - Készpénz', 'BefizetesKp'],
      ['Bér kifizetés - Utalás', 'KifizetesUtal'],
      ['Bér kifizetés - Készpénz', 'KifizetesKp'],
      ['Költségek - Utalás', 'KoltsegUt'],
      ['Költségek - Készpénz', 'KoltsegKp'],
      ['Pénztár egyenleg', 'Penztar'],
      ['Oktatók felé tartozás', 'OktatoiTartozas'],
    ];

    const header = columns.map(([label]) => label).join(';');

    const body = filteredRows
      .map((row) =>
        columns
          .map(([, field]) => {
            const value =
              row[field] === null || row[field] === undefined
                ? ''
                : String(row[field]);

            return `"${value.replaceAll('"', '""')}"`;
          })
          .join(';'),
      )
      .join('\n');

    const csv = `${header}\n${body}`;
    const blob = new Blob([`\uFEFF${csv}`], {
      type: 'text/csv;charset=utf-8;',
    });

    const link = document.createElement('a');
    const downloadUrl = URL.createObjectURL(blob);

    link.href = downloadUrl;
    link.download = 'Eredmenykimutatas.csv';
    link.click();

    URL.revokeObjectURL(downloadUrl);

    setMessage('Export elkészült.');
  };

  const KpiCard = ({ title, value, subtitle, icon, inverseColor = false }) => {
    return (
      <Card variant='outlined' sx={{ borderRadius: 2, height: '100%' }}>
        <CardContent>
          <Stack spacing={1}>
            <Stack direction='row' justifyContent='space-between' spacing={1}>
              <Typography variant='body2' color='text.secondary'>
                {title}
              </Typography>

              <Box sx={{ color: getAmountColor(value, inverseColor) }}>
                {icon}
              </Box>
            </Stack>

            <Typography
              variant='h5'
              sx={{
                fontWeight: 800,
                color: getAmountColor(value, inverseColor),
              }}
            >
              {formatMoney(value)}
            </Typography>

            {subtitle && (
              <Typography variant='caption' color='text.secondary'>
                {subtitle}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const AmountCell = ({ value, inverseColor = false }) => {
    return (
      <TableCell align='right'>
        <Typography
          component='span'
          sx={{
            fontWeight: 700,
            color: getAmountColor(value, inverseColor),
            whiteSpace: 'nowrap',
          }}
        >
          {formatMoney(value)}
        </Typography>
      </TableCell>
    );
  };

  return (
    <Fragment>
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', lg: 'row' }}
                  alignItems={{ xs: 'stretch', lg: 'center' }}
                  justifyContent='space-between'
                  spacing={2}
                >
                  <Box>
                    <Typography variant='h5' sx={{ fontWeight: 800 }}>
                      Eredménykimutatás
                    </Typography>

                    <Typography variant='body2' color='text.secondary'>
                      Bevétel, kiadás, cash-flow, kintlévőség és pénztár
                      időszakonként.
                    </Typography>
                  </Box>

                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                  >
                    <ToggleButtonGroup
                      size='small'
                      exclusive
                      value={groupMode}
                      onChange={(event, value) => {
                        if (value) {
                          setGroupMode(value);
                        }
                      }}
                    >
                      <ToggleButton value='schoolYear'>Tanév</ToggleButton>
                      <ToggleButton value='calendarYear'>Év</ToggleButton>
                      <ToggleButton value='none'>Nincs</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField
                      label='Keresés'
                      size='small'
                      value={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                      sx={{ minWidth: 260 }}
                    />

                    <Button
                      variant='outlined'
                      startIcon={<RefreshIcon />}
                      onClick={loadData}
                      disabled={loading}
                    >
                      Frissítés
                    </Button>

                    <Button
                      variant='outlined'
                      startIcon={<FileDownloadIcon />}
                      onClick={exportRows}
                      disabled={loading || filteredRows.length === 0}
                    >
                      Export CSV
                    </Button>
                  </Stack>
                </Stack>

                {latestRow && (
                  <Alert severity='info'>
                    Legutóbbi időszak: <strong>{latestRow.idoszak}</strong> —
                    eredmény: <strong>{formatMoney(latestRow.Eredmeny)}</strong>
                    , cash-flow:{' '}
                    <strong>{formatMoney(latestRow.CashFlow)}</strong>
                  </Alert>
                )}

                {error && (
                  <Alert severity='error' onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(5, 1fr)',
              },
              gap: 2,
            }}
          >
            <KpiCard
              title='Összes eredmény'
              value={totals.Eredmeny}
              subtitle='Óradíjak - bérek - költségek - veszteség'
              icon={<TrendingUpIcon />}
            />

            <KpiCard
              title='Összes cash-flow'
              value={totals.CashFlow}
              subtitle='Befizetések - kiadások'
              icon={<AccountBalanceWalletIcon />}
            />

            <KpiCard
              title='Kintlévőség'
              value={totals.Kintlevoseg}
              subtitle='Még nem befolyt összeg'
              icon={<ReceiptLongIcon />}
              inverseColor
            />

            <KpiCard
              title='Pénztár'
              value={totals.Penztar}
              subtitle='Pénztár egyenleg összesen'
              icon={<AccountBalanceWalletIcon />}
            />

            <KpiCard
              title='Oktatói tartozás'
              value={totals.OktatoiTartozas}
              subtitle='Oktatók felé tartozás'
              icon={<WarningAmberIcon />}
              inverseColor
            />
          </Box>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <Chip label={`${filteredRows.length} időszak`} variant='outlined' />
            <Chip
              label={`Óradíjak: ${formatMoney(totals.Oradijak)}`}
              color='success'
              variant='outlined'
            />
            <Chip
              label={`Bérek: ${formatMoney(totals.Berek)}`}
              color='warning'
              variant='outlined'
            />
            <Chip
              label={`Költségek: ${formatMoney(totals.Koltsegek)}`}
              color='warning'
              variant='outlined'
            />
            <Chip
              label={`Támogatás: ${formatMoney(totals.Tamogatas)}`}
              variant='outlined'
            />
            <Chip
              label={`Veszteség: ${formatMoney(totals.Veszteseg)}`}
              color='warning'
              variant='outlined'
            />
          </Stack>

          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  variant='outlined'
                  sx={{ overflow: 'auto' }}
                >
                  <Table size='small' stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell rowSpan={2}>
                          Időszak
                          {groupMode === 'schoolYear' && (
                            <Typography
                              variant='caption'
                              display='block'
                              color='text.secondary'
                            >
                              tanév szerint
                            </Typography>
                          )}
                          {groupMode === 'calendarYear' && (
                            <Typography
                              variant='caption'
                              display='block'
                              color='text.secondary'
                            >
                              év szerint
                            </Typography>
                          )}
                        </TableCell>{' '}
                        <TableCell colSpan={4} align='center'>
                          Eredmény szemlélet
                        </TableCell>
                        <TableCell colSpan={3} align='center'>
                          Cash-flow szemlélet
                        </TableCell>
                        <TableCell colSpan={4} align='center'>
                          Követelések / támogatás / veszteség
                        </TableCell>
                        <TableCell colSpan={6} align='center'>
                          Fizetési bontások
                        </TableCell>
                        <TableCell colSpan={2} align='center'>
                          Egyenlegek
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell align='right'>Óradíjak</TableCell>
                        <TableCell align='right'>Bérek</TableCell>
                        <TableCell align='right'>Költségek</TableCell>
                        <TableCell align='right'>Eredmény</TableCell>

                        <TableCell align='right'>Befizetések</TableCell>
                        <TableCell align='right'>Kiadások</TableCell>
                        <TableCell align='right'>Cash-flow</TableCell>

                        <TableCell align='right'>Kintlévőség</TableCell>
                        <TableCell align='right'>Támogatás</TableCell>
                        <TableCell align='right'>Veszteség</TableCell>

                        <TableCell align='right'>Kupon felhasználás</TableCell>

                        <TableCell align='right'>Bef. utalás</TableCell>
                        <TableCell align='right'>Bef. kp</TableCell>
                        <TableCell align='right'>Bér utalás</TableCell>
                        <TableCell align='right'>Bér kp</TableCell>
                        <TableCell align='right'>Költség utalás</TableCell>
                        <TableCell align='right'>Költség kp</TableCell>

                        <TableCell align='right'>Pénztár</TableCell>
                        <TableCell align='right'>Oktatói tartozás</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {groupedEntries.map(([groupLabel, groupRows]) => {
                        const groupTotals = getGroupTotals(groupRows);
                        const showGroupHeader = groupMode !== 'none';

                        return (
                          <Fragment key={groupLabel || 'all'}>
                            {showGroupHeader && (
                              <TableRow
                                sx={{
                                  '& td': {
                                    fontWeight: 800,
                                    bgcolor: 'action.selected',
                                  },
                                }}
                              >
                                <TableCell colSpan={20}>
                                  <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={1}
                                    alignItems={{
                                      xs: 'flex-start',
                                      md: 'center',
                                    }}
                                  >
                                    <Typography
                                      variant='body2'
                                      sx={{ fontWeight: 800 }}
                                    >
                                      {getGroupTitle()}: {groupLabel}
                                    </Typography>

                                    <Chip
                                      size='small'
                                      variant='outlined'
                                      label={`${groupRows.length} időszak`}
                                    />

                                    <Chip
                                      size='small'
                                      color={
                                        groupTotals.Eredmeny >= 0
                                          ? 'success'
                                          : 'warning'
                                      }
                                      variant='outlined'
                                      label={`Eredmény: ${formatMoney(
                                        groupTotals.Eredmeny,
                                      )}`}
                                    />

                                    <Chip
                                      size='small'
                                      color={
                                        groupTotals.CashFlow >= 0
                                          ? 'success'
                                          : 'warning'
                                      }
                                      variant='outlined'
                                      label={`Cash-flow: ${formatMoney(
                                        groupTotals.CashFlow,
                                      )}`}
                                    />

                                    <Chip
                                      size='small'
                                      variant='outlined'
                                      label={`Kintlévőség: ${formatMoney(
                                        groupTotals.Kintlevoseg,
                                      )}`}
                                    />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            )}

                            {groupRows.map((row) => (
                              <TableRow key={row.idoszak} hover>
                                <TableCell
                                  sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                                >
                                  {row.idoszak}
                                </TableCell>

                                <AmountCell value={row.Oradijak} />
                                <AmountCell value={row.Berek} inverseColor />
                                <AmountCell
                                  value={row.Koltsegek}
                                  inverseColor
                                />
                                <AmountCell value={row.Eredmeny} />

                                <AmountCell value={row.befizetes_sum} />
                                <AmountCell
                                  value={row.kiadasok_sum}
                                  inverseColor
                                />
                                <AmountCell value={row.CashFlow} />

                                <AmountCell
                                  value={row.Kintlevoseg}
                                  inverseColor
                                />
                                <AmountCell value={row.Tamogatas} />
                                <AmountCell
                                  value={row.Veszteseg}
                                  inverseColor
                                />

                                <AmountCell value={row.BefizetesKup} />

                                <AmountCell value={row.BefizetesUtal} />
                                <AmountCell value={row.BefizetesKp} />
                                <AmountCell
                                  value={row.KifizetesUtal}
                                  inverseColor
                                />
                                <AmountCell
                                  value={row.KifizetesKp}
                                  inverseColor
                                />
                                <AmountCell
                                  value={row.KoltsegUt}
                                  inverseColor
                                />
                                <AmountCell
                                  value={row.KoltsegKp}
                                  inverseColor
                                />

                                <AmountCell value={row.Penztar} />
                                <AmountCell
                                  value={row.OktatoiTartozas}
                                  inverseColor
                                />
                              </TableRow>
                            ))}

                            {showGroupHeader && (
                              <TableRow
                                sx={{
                                  '& td': {
                                    fontWeight: 800,
                                    bgcolor: 'action.hover',
                                  },
                                }}
                              >
                                <TableCell>{groupLabel} összesen</TableCell>

                                <AmountCell value={groupTotals.Oradijak} />
                                <AmountCell
                                  value={groupTotals.Berek}
                                  inverseColor
                                />
                                <AmountCell
                                  value={groupTotals.Koltsegek}
                                  inverseColor
                                />
                                <AmountCell value={groupTotals.Eredmeny} />

                                <AmountCell value={groupTotals.befizetes_sum} />
                                <AmountCell
                                  value={groupTotals.kiadasok_sum}
                                  inverseColor
                                />
                                <AmountCell value={groupTotals.CashFlow} />

                                <AmountCell
                                  value={groupTotals.Kintlevoseg}
                                  inverseColor
                                />
                                <AmountCell value={groupTotals.Tamogatas} />
                                <AmountCell
                                  value={groupTotals.Veszteseg}
                                  inverseColor
                                />

                                <AmountCell value={groupTotals.BefizetesKup} />

                                <AmountCell value={groupTotals.BefizetesUtal} />
                                <AmountCell value={groupTotals.BefizetesKp} />
                                <AmountCell
                                  value={groupTotals.KifizetesUtal}
                                  inverseColor
                                />
                                <AmountCell
                                  value={groupTotals.KifizetesKp}
                                  inverseColor
                                />
                                <AmountCell
                                  value={groupTotals.KoltsegUt}
                                  inverseColor
                                />
                                <AmountCell
                                  value={groupTotals.KoltsegKp}
                                  inverseColor
                                />

                                <AmountCell value={groupTotals.Penztar} />
                                <AmountCell
                                  value={groupTotals.OktatoiTartozas}
                                  inverseColor
                                />
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })}

                      {filteredRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={20} align='center'>
                            Nincs megjeleníthető eredménykimutatás adat.
                          </TableCell>
                        </TableRow>
                      )}

                      {filteredRows.length > 0 && (
                        <TableRow
                          sx={{
                            '& td': {
                              fontWeight: 800,
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <TableCell>Összesen</TableCell>

                          <AmountCell value={totals.Oradijak} />
                          <AmountCell value={totals.Berek} inverseColor />
                          <AmountCell value={totals.Koltsegek} inverseColor />
                          <AmountCell value={totals.Eredmeny} />

                          <AmountCell value={totals.befizetes_sum} />
                          <AmountCell
                            value={totals.kiadasok_sum}
                            inverseColor
                          />
                          <AmountCell value={totals.CashFlow} />

                          <AmountCell value={totals.Kintlevoseg} inverseColor />
                          <AmountCell value={totals.Tamogatas} />
                          <AmountCell value={totals.Veszteseg} inverseColor />

                          <AmountCell value={totals.BefizetesKup} />

                          <AmountCell value={totals.BefizetesUtal} />
                          <AmountCell value={totals.BefizetesKp} />
                          <AmountCell
                            value={totals.KifizetesUtal}
                            inverseColor
                          />
                          <AmountCell value={totals.KifizetesKp} inverseColor />
                          <AmountCell value={totals.KoltsegUt} inverseColor />
                          <AmountCell value={totals.KoltsegKp} inverseColor />

                          <AmountCell value={totals.Penztar} />
                          <AmountCell
                            value={totals.OktatoiTartozas}
                            inverseColor
                          />
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={3000}
        onClose={() => setMessage(null)}
        message={message}
      />
    </Fragment>
  );
};

FormEredmeny.propTypes = {
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, { logout })(withRouter(FormEredmeny));
